import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Download, FileText, Loader2, Printer } from 'lucide-react'
import {
  Bidi, Card, CardHeader, EmptyState, ErrorState, LoadingState,
  NoPermissionState, useLocale, type AppLocale,
} from '@hk/ui'
import {
  ApiError, fetchReportPdf, fetchReports, renderReport, reportName, reportPurpose,
  translateError,
  type ReportDefinition, type ReportDocument, type ReportParameters,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-110 · REPORTS. One screen, and every report there will ever be.

   THIS SCREEN KNOWS NO REPORT BY NAME. The list comes from the server, already filtered
   to what this person may actually run, and the document comes back as markup. When the
   remaining seven definitions are settled with the client they appear here without a
   line of code changing — which is the whole reason reporting was built as a framework
   and a registry rather than as eight screens.

   THE PREVIEW IS THE REPORT. Not a table that resembles it: the same markup that goes
   through a browser on the server to become the PDF. A screen that drew its own table
   would be a second renderer, and the two would disagree — quietly, in a column nobody
   checked, six weeks after anybody remembered there were two of them.

   AND IT IS DRAWN INSIDE A SANDBOX. See the frame below; the reasoning is there.

   THE DATE BOXES START EMPTY, AND THAT IS DELIBERATE. Only the server knows what today
   is in Najaf. A browser filling in "the last seven days" would be reading a laptop
   clock that may be set to London, and would offer a supervisor the wrong week for three
   hours every evening. So the screen asks for no period at all, the server chooses one,
   and the boxes are then filled from the period the server SAYS it used.
   ========================================================================= */

export function Reports({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [code, setCode] = useState<string | null>(null)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  /* THE LANGUAGE OF THE DOCUMENT, which is not always the language of the reader: a
     supervisor who works in Arabic still has to hand a contractor something they can
     read. It follows whatever they are reading, because that is right nearly every time,
     and it is a control rather than an assumption because nearly is not always.

     NULL MEANS NOBODY HAS CHOSEN — the same distinction the roster draws between a rest
     day and an unwritten cell, and it matters for the same reason. Held as a copy of the
     locale, the control would freeze at whatever the shell happened to be on the first
     time this screen was opened: switch the whole application to Arabic afterwards and
     the report would still be prepared in English, with the button beside it saying so
     and nobody looking. Held as "unchosen", it follows the reader until the moment they
     say otherwise, and then stops following. */
  const [chosenLanguage, setChosenLanguage] = useState<AppLocale | null>(null)
  const language = chosenLanguage ?? locale
  /* THE DOCUMENT BEING READ, held here rather than read off the mutation.
     `useMutation` clears its own `data` the moment a new run starts, so a preview taken
     from it would vanish the instant somebody asked for a period the server refuses —
     leaving them with a refusal, no report, and no way back to the one they were reading
     except to remember what they had typed. Held in state, a refused run costs nothing. */
  const [rendered, setRendered] = useState<ReportDocument | null>(null)

  const catalogue = useQuery({
    queryKey: ['reports', propertyId],
    queryFn: ({ signal }) => fetchReports(propertyId, { signal }),
    retry: false,
  })

  const selected = useMemo(
    () => (catalogue.data ?? []).find((r) => r.code === code) ?? null,
    [catalogue.data, code])

  const render = useMutation({
    mutationFn: (parameters: ReportParameters & { code: string }) =>
      renderReport(propertyId, parameters.code, parameters),
    onSuccess: (document) => {
      setRendered(document)
      /* THE BOXES FOLLOW THE DOCUMENT, never the other way round. This is the only place
         they are written to by anything but the person, and it is what stops the form
         above the report saying one period while the letterhead inside it says another. */
      if (document.periodFrom) setFrom(document.periodFrom)
      if (document.periodTo) setTo(document.periodTo)
    },
  })

  const print = useMutation({
    mutationFn: async (document: ReportDocument) => {
      const pdf = await fetchReportPdf(propertyId, document.code, {
        /* THE PERIOD ON SCREEN, not the boxes. Somebody who typed a new date and did not
           press Run would otherwise download a document that does not match the one they
           were looking at — and would have no way of telling. */
        ...(document.periodFrom ? { from: document.periodFrom } : {}),
        ...(document.periodTo ? { to: document.periodTo } : {}),
        language: document.language,
      })
      save(pdf, document.fileName)
    },
  })

  /* A document from the LAST report is not a preview of this one. Cleared on the
     selection rather than on the render, so the old report does not sit under the new
     one's title for as long as the new one takes to come back. */
  useEffect(() => {
    setRendered(null)
    render.reset()
    print.reset()
  }, [code])

  if (catalogue.isLoading && !catalogue.error) return <LoadingState rows={4} />

  if (catalogue.error) {
    if (catalogue.error instanceof ApiError && catalogue.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(catalogue.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={catalogue.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  /* NOT AN ERROR, AND NOT A BLANK SCREEN EITHER. An empty catalogue means this person
     holds no reporting grant — the same fact the sidebar used to decide whether to draw
     the link, arrived at independently. It is worth saying plainly rather than showing
     an empty picker that looks like a server that has lost its reports. */
  if ((catalogue.data ?? []).length === 0)
    return <EmptyState title={t.reportsNone} body={t.reportsNoneBody} />

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader title={t.reportsPick} icon={<FileText className="size-4" />} />
        <div className="grid gap-2 p-3 sm:grid-cols-2 xl:grid-cols-3">
          {catalogue.data!.map((report) => (
            <ReportChoice
              key={report.code}
              report={report}
              locale={locale}
              chosen={report.code === code}
              onChoose={() => setCode(report.code)}
            />
          ))}
        </div>
      </Card>

      {selected && (
        <Card>
          <CardHeader title={reportName(selected, locale)} icon={<Printer className="size-4" />} />
          <div className="flex flex-wrap items-end gap-3 p-3">
            {/* Only a PERIOD report gets date boxes. A moment report is a photograph of
                now: offering to date it would promise something it cannot do. */}
            {selected.isPeriod && (
              <>
                <DateBox label={t.reportsFrom} value={from} onChange={setFrom} />
                <DateBox label={t.reportsTo} value={to} onChange={setTo} />
              </>
            )}

            <fieldset className="flex flex-col gap-1">
              <legend className="sr-only">{t.reportsLanguage}</legend>
              <span className="text-[11px] font-medium text-muted-foreground">
                {t.reportsLanguage}
              </span>
              <div className="flex items-center gap-1.5">
                <LanguageButton
                  label="English" chosen={language === 'en'}
                  onChoose={() => setChosenLanguage('en')} />
                <LanguageButton
                  label="العربية" chosen={language === 'ar'}
                  onChoose={() => setChosenLanguage('ar')} />
              </div>
            </fieldset>

            <button
              type="button"
              disabled={render.isPending}
              onClick={() => render.mutate({
                code: selected.code,
                ...(selected.isPeriod && from ? { from } : {}),
                ...(selected.isPeriod && to ? { to } : {}),
                language,
              })}
              className="inline-flex items-center gap-1.5 rounded-hk bg-[var(--hk-primary)]
                         px-3 py-1.5 text-xs font-medium text-white
                         disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            >
              {render.isPending && <Loader2 className="size-3.5 animate-spin" />}
              {t.reportsRun}
            </button>

            {/* DOWNLOAD IS SHUT UNTIL SOMETHING HAS BEEN RUN, so nobody ever prints a
                document they have not looked at — and so the printed copy and the copy
                on screen are the same period rather than two guesses at it. */}
            <button
              type="button"
              disabled={!rendered || print.isPending}
              onClick={() => rendered && print.mutate(rendered)}
              className="inline-flex items-center gap-1.5 rounded-hk border border-border
                         px-3 py-1.5 text-xs font-medium hover:bg-muted
                         disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {print.isPending
                ? <Loader2 className="size-3.5 animate-spin" />
                : <Download className="size-3.5" />}
              {t.reportsDownload}
            </button>
          </div>

          {/* THE REFUSALS ARE DRAWN SEPARATELY, and the preview is not thrown away for
              either of them. A period the server would not accept, and a server with no
              browser installed on it, are both things you fix and try again — and losing
              the document you were already reading in order to be told so is a punishment
              for asking. */}
          {render.error && <Refusal message={translateError(render.error, locale).message} />}
          {print.error && <Refusal message={translateError(print.error, locale).message} />}
        </Card>
      )}

      {rendered && <Preview document={rendered} label={t.reportsPreview} />}
    </div>
  )
}

/* ── the preview ──────────────────────────────────────────────────────────── */

/**
 * THE DOCUMENT, IN A SANDBOX.
 *
 * The markup comes from our own server, so this is not a defence against the server —
 * it is a defence against the report being a route by which markup reaches the
 * application's own document. A report is built from names people typed: a room's
 * remarks, an attendant's name, a stock item somebody added. Every one of those is
 * escaped on the way into the document, and `sandbox` is the second lock, which is the
 * one that still holds on the day the first is forgotten in a new report six months from
 * now. `dangerouslySetInnerHTML` here would put all eight reports' escaping on the
 * critical path of the application's own origin.
 *
 * `sandbox` with NO VALUE is the whole point: no scripts, no forms, no navigation, and —
 * the one that matters — a unique opaque origin, so the frame cannot read this
 * document's cookies, storage or session. The report needs none of them. It is a page of
 * type, a table and an embedded font, and all three render under every restriction.
 */
function Preview({ document, label }: { document: ReportDocument; label: string }) {
  return (
    <Card>
      <CardHeader title={label} icon={<FileText className="size-4" />} />
      {/* A SHEET OF PAPER ON A DESK. The frame is white and the report is A4, so without
          a surround it bleeds edge to edge and stops looking like the thing that is about
          to come out of a printer.

          THE HEIGHT IS FIXED, and it has to be: measuring the content would mean reading
          inside the frame, and the sandbox that makes the frame safe is exactly what
          stops us. A fixed page that scrolls is the honest trade — the alternative is
          `allow-same-origin`, which gives the height back by giving up the isolation. */}
      <div className="bg-muted/50 p-3">
        <iframe
          // Keyed on the render, so a new document replaces the frame rather than
          // reusing one already scrolled halfway down the previous report.
          key={`${document.code}|${document.generatedAtUtc}|${document.language}`}
          title={document.title}
          srcDoc={document.html}
          sandbox=""
          className="mx-auto block h-[68vh] w-full max-w-[62rem] rounded-hk border border-border bg-white shadow-sm"
        />
      </div>
    </Card>
  )
}

/* ── the pieces ───────────────────────────────────────────────────────────── */

function ReportChoice({
  report, locale, chosen, onChoose,
}: {
  report: ReportDefinition; locale: AppLocale; chosen: boolean; onChoose: () => void
}) {
  return (
    <button
      type="button"
      onClick={onChoose}
      aria-pressed={chosen}
      className={`flex flex-col items-start gap-1 rounded-hk border p-2.5 text-start
                  cursor-pointer hover:bg-muted
                  ${chosen
                    ? 'border-[var(--hk-primary)] bg-[var(--hk-primary)]/5'
                    : 'border-border'}`}
    >
      <span className="text-[13px] font-medium">
        <Bidi>{reportName(report, locale)}</Bidi>
      </span>
      {/* WHAT QUESTION IT ANSWERS, on the picker itself. A list of eight report titles
          is a list somebody opens four of to find out which one they meant. */}
      <span className="text-[11.5px] leading-snug text-muted-foreground">
        <Bidi>{reportPurpose(report, locale)}</Bidi>
      </span>
    </button>
  )
}

function DateBox({
  label, value, onChange,
}: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
      {/* `type="date"` is not cosmetic here. Its value is ALWAYS `yyyy-mm-dd` whatever
          the browser displays — which is exactly the wire format the server wants, so
          nothing between this box and the query string has to parse a date. */}
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        className="rounded-hk border border-border bg-background px-2 py-1 text-xs"
      />
    </label>
  )
}

function LanguageButton({
  label, chosen, onChoose,
}: { label: string; chosen: boolean; onChoose: () => void }) {
  return (
    <button
      type="button"
      onClick={onChoose}
      aria-pressed={chosen}
      className={`rounded-hk border px-2.5 py-1 text-xs font-medium cursor-pointer
                  ${chosen
                    ? 'border-[var(--hk-primary)] bg-[var(--hk-primary)]/10'
                    : 'border-border hover:bg-muted'}`}
    >
      {label}
    </button>
  )
}

function Refusal({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="border-t border-border bg-[#fef2f2] px-3 py-2 text-[12.5px] text-[#991b1b]
                 dark:bg-[#2a1416] dark:text-[#fca5a5]"
    >
      {message}
    </div>
  )
}

/* ── saving the file ──────────────────────────────────────────────────────── */

/**
 * THE ONE PIECE OF DOM IN THIS FILE, and it is here rather than in `@hk/core` because
 * core must stay importable from a handset that has no `document`.
 *
 * The object URL is revoked. A report is megabytes, and a supervisor who prints six of
 * them over a morning would otherwise be holding all six until they closed the tab.
 */
function save(pdf: Blob, fileName: string): void {
  const url = URL.createObjectURL(pdf)
  try {
    const link = window.document.createElement('a')
    link.href = url
    link.download = fileName
    link.rel = 'noopener'
    window.document.body.appendChild(link)
    link.click()
    link.remove()
  } finally {
    // Not immediately: the browser needs the URL to still resolve while it starts the
    // download. A revoke on the next turn of the loop is late enough everywhere and
    // early enough that nothing is held for the life of the page.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}
